import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { ApiError } from '@/lib/apiClient'
import { formatDateTime } from '@/lib/datetime'
import { cn } from '@/lib/cn'

import { useAdminMutation, useAdminQuery } from '../api'
import { useAdminContext } from '../session'
import type { AdminOrder, OrderConfirmationResult, TableTypeChoice } from '../types'

/**
 * La suite d'une commande, sur sa fiche : confirmation, table, bracelets.
 *
 * Trois cas réels :
 *
 * * une table achetée en ligne, encaissée hors ligne — et aucun bouton pour
 *   envoyer au client la confirmation de sa réservation (« Envoyer /
 *   Renvoyer la confirmation ») ;
 * * une table vendue sans réservation, donc absente du placement
 *   (« Définir le type de table ») ;
 * * un client qui écrit pour changer ses bracelets célibataire.
 */

const invalidateKeys = (eventId: string | undefined) => [
  ['admin-orders', eventId],
  ['admin-reservations', eventId],
  ['admin-reservation', eventId],
  ['admin-table-request-for-order', eventId],
]

function useSendConfirmation(orderId: string) {
  const { eventId } = useAdminContext()
  return useAdminMutation<OrderConfirmationResult, Record<string, never>>(
    'post',
    () => `/admin/events/${eventId}/orders/${orderId}/send-confirmation`,
    eventId,
    invalidateKeys(eventId),
  )
}

function DeliveryLine({ result }: { result: OrderConfirmationResult }) {
  const { t } = useTranslation()
  return (
    <p
      role="status"
      className={cn(
        'mt-2 break-words text-sm',
        result.delivery === 'sent' ? 'text-state-success' : 'text-state-danger',
      )}
    >
      {t(`admin.tableRequests.delivery.${result.delivery}`, {
        email: result.recipient,
        error: result.error ?? '',
      })}
    </p>
  )
}

/**
 * « Envoyer / Renvoyer la confirmation » d'une commande réglée.
 *
 * Une table reçoit la confirmation de réservation complète (remerciements,
 * table, chaque convive, ses bouteilles, QR) ; le pointage hors ligne ne
 * l'envoie pas lui-même. Toute autre commande reçoit de nouveau celle de son
 * règlement. Une table née d'une demande a ce bouton dans le bloc de sa
 * demande, plus haut : on ne l'affiche pas deux fois.
 */
export function OrderConfirmation({ order }: { order: AdminOrder }) {
  const { t, i18n } = useTranslation()
  const { event, can } = useAdminContext()
  const send = useSendConfirmation(order.id)
  const table = order.confirmation_kind === 'table'
  // Une table réglée à l'entrée reçoit sa confirmation avant d'être encaissée :
  // QR, noms, bouteilles et « À régler à l'entrée : X € ».
  const atDoor = table && order.status === 'pending_payment' && Boolean(order.pays_at_door)
  if ((order.status !== 'paid' && !atDoor) || order.from_table_request) return null
  if (!can('orders.payment.mark_paid')) return null

  const sentAt = send.data?.order.confirmation_sent_at ?? order.confirmation_sent_at
  const sentBy = send.data?.order.confirmation_sent_by_label ?? order.confirmation_sent_by_label

  return (
    <section className="mt-6 border-t border-line-subtle pt-5">
      <p className="text-caption uppercase tracking-wide text-content-muted">
        {t('admin.orders.confirmation.title')}
      </p>
      <p className="mt-1 text-xs text-content-muted">
        {atDoor
          ? t('admin.orders.confirmation.atDoorHint')
          : table
            ? t('admin.orders.confirmation.tableHint')
            : t('admin.orders.confirmation.orderHint')}
      </p>
      <Button
        size="sm"
        variant={table && !sentAt ? 'champagne' : 'outline'}
        className="mt-3"
        disabled={send.isPending}
        onClick={() => send.mutate({})}
      >
        {send.isPending
          ? t('admin.tableRequests.sending')
          : sentAt
            ? table
              ? t('admin.orders.confirmation.resendTable')
              : t('admin.orders.confirmation.resend')
            : table
              ? t('admin.orders.confirmation.sendTable')
              : t('admin.orders.confirmation.send')}
      </Button>
      {sentAt ? (
        <p className="mt-2 text-caption text-content-muted">
          {t('admin.tableRequests.lastSent', {
            date: formatDateTime(sentAt, i18n.language, event?.timezone ?? 'UTC'),
            who: sentBy ?? '—',
          })}
        </p>
      ) : table ? (
        <p className="mt-2 text-caption text-state-warning">
          {t('admin.orders.confirmation.notSentYet')}
        </p>
      ) : null}
      {send.data ? <DeliveryLine result={send.data} /> : null}
      {send.error instanceof ApiError ? (
        <p className="mt-2 text-sm text-state-danger">{send.error.message}</p>
      ) : null}
    </section>
  )
}

/**
 * Juste après « Encaisser hors ligne » d'une table : la question qu'on se
 * pose aussitôt. Affichée au-dessus de la liste, et non dans la fiche : une
 * liste filtrée sur « non réglées » ne montre plus la commande qu'on vient
 * de régler, et la question disparaîtrait avec elle.
 */
export function PaidTablePrompt({
  order,
  onClose,
}: {
  order: { id: string; order_number: string }
  onClose: () => void
}) {
  const { t } = useTranslation()
  const ref = useRef<HTMLDivElement>(null)
  const send = useSendConfirmation(order.id)

  useEffect(() => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [order.id])

  return (
    <div
      ref={ref}
      role="status"
      className="mb-4 rounded-md border border-champagne-400/40 bg-champagne-400/5 px-3 py-3"
    >
      <p className="break-words text-sm text-content-primary">
        {t('admin.orders.confirmation.prompt', { number: order.order_number })}
      </p>
      {send.data ? (
        <>
          <DeliveryLine result={send.data} />
          <Button size="sm" variant="ghost" className="mt-2" onClick={onClose}>
            {t('admin.orders.confirmation.close')}
          </Button>
        </>
      ) : (
        <div className="mt-2 flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="champagne"
            disabled={send.isPending}
            onClick={() => send.mutate({})}
          >
            {send.isPending ? t('admin.tableRequests.sending') : t('admin.tableRequests.sendNowYes')}
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose}>
            {t('admin.tableRequests.sendNowLater')}
          </Button>
        </div>
      )}
      {send.error instanceof ApiError ? (
        <p className="mt-2 text-sm text-state-danger">{send.error.message}</p>
      ) : null}
    </div>
  )
}

/**
 * « Définir le type de table » : une ligne de table sans réservation.
 *
 * Sans réservation, la table n'existe ni au placement ni au contrôle. On
 * choisit son type (« Sur mesure » d'office s'il existe) et le nom du groupe ;
 * la réservation est créée comme la vente l'aurait fait. Aucun montant ne
 * bouge — l'écran le dit avant le clic.
 */
export function DefineTableType({ order }: { order: AdminOrder }) {
  const { t } = useTranslation()
  const { eventId, can } = useAdminContext()
  const canWrite = can('tables.reservation.update')
  const types = useAdminQuery<TableTypeChoice[]>(
    ['admin-reservation-table-types', eventId],
    `/admin/events/${eventId}/reservations/table-types`,
    eventId,
    { enabled: canWrite && Boolean(order.needs_table_type) },
  )
  const define = useAdminMutation<
    AdminOrder,
    { table_type_id: string; name: string | null; party_size: number | null }
  >(
    'post',
    () => `/admin/events/${eventId}/orders/${order.id}/table-reservation`,
    eventId,
    [...invalidateKeys(eventId), ['admin-table-requests', eventId]],
  )
  const [typeId, setTypeId] = useState('')
  const [name, setName] = useState('')
  // Le nombre de convives que la commande connaît encore ; sinon on le
  // demande — deviner placerait un groupe de six à une table de deux.
  const [size, setSize] = useState(
    order.table_party_size ? String(order.table_party_size) : '',
  )
  if (!order.needs_table_type) return null

  const options = types.data ?? []
  const suggested = options.find((option) => option.suggested) ?? options[0] ?? null
  const chosenId = typeId || suggested?.id || ''
  const chosen = options.find((option) => option.id === chosenId) ?? null
  const parsed = size.trim() ? Number.parseInt(size, 10) : null
  const sizeValid =
    parsed !== null &&
    Number.isFinite(parsed) &&
    chosen !== null &&
    parsed >= chosen.capacity_min &&
    parsed <= chosen.capacity_max

  return (
    <section
      role="alert"
      className="mt-6 space-y-3 rounded-md border border-state-warning/40 bg-state-warning/5 px-3 py-3"
    >
      <p className="text-sm text-state-warning">{t('admin.orders.tableType.notice')}</p>
      {canWrite ? (
        options.length === 0 && !types.isPending ? (
          <p className="text-sm text-state-warning">{t('admin.tableRequests.noTableType')}</p>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              <label className="text-caption text-content-muted">
                {t('admin.tableRequests.tableType')}
                <select
                  value={chosenId}
                  onChange={(changeEvent) => setTypeId(changeEvent.target.value)}
                  className={INPUT}
                >
                  {options.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label} ({option.capacity_min}–{option.capacity_max})
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-caption text-content-muted">
                {t('admin.tableRequests.groupName')}
                <input
                  value={name}
                  maxLength={120}
                  placeholder={t('admin.tableRequests.groupNamePlaceholder')}
                  onChange={(changeEvent) => setName(changeEvent.target.value)}
                  className={INPUT}
                />
              </label>
              <label className="text-caption text-content-muted">
                {t('admin.orders.tableType.partySize')}
                <input
                  type="number"
                  inputMode="numeric"
                  min={chosen?.capacity_min ?? 1}
                  max={chosen?.capacity_max ?? 200}
                  value={size}
                  onChange={(changeEvent) => setSize(changeEvent.target.value)}
                  className={cn(INPUT, 'tabular-nums', !sizeValid && 'border-state-danger')}
                />
              </label>
            </div>
            {chosen && parsed !== null && !sizeValid ? (
              <p className="text-sm text-state-warning">
                {t('admin.tableRequests.typeCapacity', {
                  min: chosen.capacity_min,
                  max: chosen.capacity_max,
                  count: parsed,
                })}
              </p>
            ) : null}
            <div className="flex flex-wrap items-center gap-3">
              <Button
                size="sm"
                variant="champagne"
                disabled={define.isPending || !chosen || !sizeValid}
                onClick={() =>
                  define.mutate({
                    table_type_id: chosenId,
                    name: name.trim() || null,
                    party_size: parsed,
                  })
                }
              >
                {define.isPending
                  ? t('admin.orders.tableType.saving')
                  : t('admin.orders.tableType.action')}
              </Button>
              <span className="text-caption text-content-muted">
                {t('admin.tableRequests.backfill.moneyUnchanged')}
              </span>
            </div>
          </>
        )
      ) : null}
      {define.error instanceof ApiError ? (
        <p className="text-sm text-state-danger">{define.error.message}</p>
      ) : null}
    </section>
  )
}

/**
 * Les bracelets célibataire de la commande : qui en attend un, et combien.
 *
 * Réglables personne par personne, pour le client qui écrit « finalement,
 * pas de bracelet pour Paul ». La porte et « Mon Masked Night » lisent le
 * même choix. Une personne déjà entrée (ou dont le bracelet est déjà au
 * poignet) se règle à la porte : sa case est figée.
 */
export function OrderBracelets({ order }: { order: AdminOrder }) {
  const { t } = useTranslation()
  const { eventId, can } = useAdminContext()
  const people = order.bracelets ?? []
  const saved = Object.fromEntries(people.map((person) => [person.key, person.wanted]))
  const [wanted, setWanted] = useState<Record<string, boolean>>(saved)
  const [reason, setReason] = useState('')
  const signature = people.map((person) => `${person.key}:${person.wanted}`).join('|')
  useEffect(() => {
    setWanted(Object.fromEntries(people.map((person) => [person.key, person.wanted])))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature])
  const save = useAdminMutation<
    AdminOrder,
    { people: { key: string; wanted: boolean }[]; reason: string | null }
  >(
    'put',
    () => `/admin/events/${eventId}/orders/${order.id}/bracelets`,
    eventId,
    invalidateKeys(eventId),
  )

  if (!order.bracelets_enabled || people.length === 0) return null
  const canWrite = can('single.bracelet.update')
  const closed = ['cancelled', 'expired', 'refunded'].includes(order.status)
  const changes = people
    .filter((person) => wanted[person.key] !== person.wanted)
    .map((person) => ({ key: person.key, wanted: Boolean(wanted[person.key]) }))
  const count = people.filter((person) => wanted[person.key]).length

  return (
    <section className="mt-6 border-t border-line-subtle pt-5">
      <p className="text-caption uppercase tracking-wide text-content-muted">
        {t('admin.orders.bracelets.title', { count: order.bracelets_wanted ?? 0 })}
      </p>
      <p className="mt-1 text-xs text-content-muted">{t('admin.orders.bracelets.hint')}</p>
      <ul className="mt-3 space-y-1.5">
        {people.map((person) => (
          <li key={person.key}>
            <label className="flex min-h-10 items-center gap-3 rounded-md border border-line-subtle px-3 py-2">
              <input
                type="checkbox"
                checked={Boolean(wanted[person.key])}
                disabled={!canWrite || closed || !person.editable || save.isPending}
                onChange={() =>
                  setWanted((current) => ({ ...current, [person.key]: !current[person.key] }))
                }
                className="h-4 w-4 shrink-0 accent-[var(--ember-500)]"
              />
              <span className="min-w-0 flex-1 break-words text-sm text-content-primary">
                {person.name}
              </span>
              {person.status === 'distributed' ? (
                <span className="shrink-0 text-caption text-state-success">
                  {t('admin.orders.bracelets.handed')}
                </span>
              ) : person.checked_in ? (
                <span className="shrink-0 text-caption text-content-muted">
                  {t('admin.orders.bracelets.atDoor')}
                </span>
              ) : null}
            </label>
          </li>
        ))}
      </ul>
      {canWrite && !closed && changes.length > 0 ? (
        <div className="mt-3 space-y-2">
          <label className="block text-caption text-content-muted">
            {t('admin.orders.bracelets.reason')}
            <input
              value={reason}
              maxLength={500}
              placeholder={t('admin.orders.bracelets.reasonPlaceholder')}
              onChange={(changeEvent) => setReason(changeEvent.target.value)}
              className={INPUT}
            />
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              disabled={save.isPending}
              onClick={() =>
                save.mutate(
                  { people: changes, reason: reason.trim() || null },
                  { onSuccess: () => setReason('') },
                )
              }
            >
              {t('admin.orders.bracelets.save', { count })}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setWanted(saved)}>
              {t('admin.project.cancel')}
            </Button>
          </div>
        </div>
      ) : null}
      {save.isSuccess && changes.length === 0 ? (
        <p role="status" className="mt-2 text-caption text-state-success">
          {t('admin.orders.bracelets.saved')}
        </p>
      ) : null}
      {save.error instanceof ApiError ? (
        <p className="mt-2 text-sm text-state-danger">{save.error.message}</p>
      ) : null}
    </section>
  )
}

const INPUT =
  'mt-1 h-10 w-full min-w-0 rounded-md border border-line bg-bg-base px-3 text-sm text-content-primary'
